<template>
  <div class="container">
    <BasicTitle :title="pageTitle" />
    <BasicSubtitle subtitle="Los campos marcados con (*) son obligatorios." />
    <div class="container-form">
      <BasicLabel label="Tipo *" />
      <BorderSelect
        v-model="form.type"
        label="Tipo *"
        :options="conceptTypeOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.type)" :message="errors.type" />

      <BasicLabel label="Nombre *" />
      <BorderInput v-model="form.name" label="Nombre *" />
      <FormError :show="Boolean(errors.name)" :message="errors.name" />

      <BasicLabel label="Descripción" />
      <BorderInput v-model="form.description" label="Descripción" type="textarea" />
    </div>
    <PrimaryButton :label="primaryButtonLabel" :click="onSave" />
  </div>
</template>

<script>
import { computed, reactive, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import store from "../../store";
import BasicTitle from "../../widgets/title/BasicTitle";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle";
import BasicLabel from "../../widgets/label/BasicLabel";
import BorderInput from "../../widgets/input/BorderInput";
import BorderSelect from "../../widgets/select/BorderSelect";
import FormError from "../../widgets/error/FormError";
import PrimaryButton from "../../widgets/button/PrimaryButton";
import { openMode, actions } from "../../constants";

const CONCEPT_TYPES = [
  { id: "income", name: "Ingreso" },
  { id: "expense", name: "Egreso" },
];

const createEmptyErrors = () => ({
  type: "",
  name: "",
});

export default {
  components: {
    BasicTitle,
    BasicSubtitle,
    BasicLabel,
    BorderInput,
    BorderSelect,
    FormError,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const isEditMode = computed(() => store.state.openMode === openMode.MODIFY);
    const pageTitle = computed(() => (isEditMode.value ? "Editar concepto" : "Nuevo concepto"));
    const primaryButtonLabel = computed(() => (isEditMode.value ? "Actualizar" : "Guardar"));

    const form = reactive({
      id: null,
      type: "",
      name: "",
      description: "",
    });

    const errors = reactive(createEmptyErrors());
    const conceptTypeOptions = computed(() => CONCEPT_TYPES);

    const selectedConceptId = computed(
      () => store.state.concept.selectedConcepts?.[0]?.id || store.state.concept.current?.id || null
    );

    const clearErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = "";
      });
    };

    const resetForm = () => {
      form.id = null;
      form.type = "";
      form.name = "";
      form.description = "";
      clearErrors();
    };

    const populateForm = (concept) => {
      if (!concept) return;
      form.id = concept.id ?? null;
      form.type = concept.type || "";
      form.name = concept.name || "";
      form.description = concept.description || "";
      clearErrors();
    };

    const loadConcept = async () => {
      if (!isEditMode.value || !selectedConceptId.value) {
        resetForm();
        return;
      }
      store.commit("setLoading", true);
      const response = await store.dispatch(actions.conceptActions.findOne, selectedConceptId.value);
      store.commit("setLoading", false);
      if (response?.ok) {
        populateForm(response.data);
      } else {
        router.push("/contabilidad/multa/listado");
        store.commit("setToast", {
          type: "error",
          message: response?.message || "No fue posible cargar el concepto seleccionado.",
        });
      }
    };

    const validate = () => {
      clearErrors();
      if (!form.type) {
        errors.type = "Seleccione el tipo";
      }
      if (!form.name) {
        errors.name = "Ingrese el nombre";
      }
      return !Object.values(errors).some(Boolean);
    };

    const onSave = async () => {
      if (!validate()) return;
      if (!buildingId.value) {
        store.commit("setToast", {
          type: "error",
          message: "Seleccione un edificio para continuar.",
        });
        return;
      }

      const payload = {
        id: form.id,
        type: form.type,
        name: form.name.trim(),
        description: form.description?.trim() || "",
        buildingId: buildingId.value,
      };

      store.commit("setLoading", true);
      try {
        let response;
        if (isEditMode.value && form.id) {
          response = await store.dispatch(actions.conceptActions.update, payload);
        } else {
          response = await store.dispatch(actions.conceptActions.create, payload);
        }
        if (response?.ok) {
          store.commit("setToast", {
            type: "success",
            message: isEditMode.value
              ? "Concepto actualizado correctamente."
              : "Concepto creado correctamente.",
          });
          store.commit("setSelectedConcepts", []);
          router.push("/contabilidad/multa/listado");
        } else {
          const duplicate = response?.status === 409;
          if (duplicate) {
            errors.name = "Ya existe un concepto con ese nombre";
          }
          store.commit("setToast", {
            type: duplicate ? "warn" : "error",
            message: response?.message || "No fue posible guardar el concepto.",
          });
        }
      } catch (error) {
        console.error(error);
        store.commit("setToast", {
          type: "error",
          message: "Ocurrió un error al guardar el concepto.",
        });
      } finally {
        store.commit("setLoading", false);
      }
    };

    onMounted(async () => {
      store.commit("setLoading", true);
      try {
        if (isEditMode.value) {
          await loadConcept();
        } else {
          resetForm();
        }
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(
      () => form.type,
      () => {
        if (form.type) errors.type = "";
      }
    );

    watch(
      () => form.name,
      () => {
        if (form.name) errors.name = "";
      }
    );

    watch(buildingId, (value, oldValue) => {
      if (value && value !== oldValue && !isEditMode.value) {
        resetForm();
      }
    });

    return {
      store,
      form,
      errors,
      conceptTypeOptions,
      pageTitle,
      primaryButtonLabel,
      onSave,
    };
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.container-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  width: 100%;
  max-width: 600px;
}

.container-form > * {
  width: 100%;
  margin-bottom: 1.5rem;
}

.container-form > *:last-child {
  margin-bottom: 0;
}
</style>
